<template>
  <div class="bj-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-blackjack" :size="18" /> {{ $t('games.blackjack.title') }}
        <span class="game-tag bj-tag">{{ $t('games.blackjack.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ active: soundEnabled }"
          :title="$t('games.toggle_sound')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas: Green felt table -->
      <div class="game-canvas-area">
        <div class="bj-table">
          <div class="bj-felt-edge"></div>

          <!-- Shoe indicator -->
          <div class="shoe-info" v-if="shoeRemaining > 0">
            {{ $t('games.blackjack.shoe_cards', { n: shoeRemaining }) }}
          </div>

          <!-- Result overlay (shown when dealer animation completes) -->
          <div class="bj-result-overlay" :class="{ active: resultVisible && payout !== null }">
            <template v-if="resultVisible && payout">
              <div
                class="bj-result-text"
                :class="{
                  win: payout.is_win,
                  loss: !payout.is_win && payout.net_cents < 0,
                  push: !payout.is_win && payout.net_cents === 0,
                }"
              >
                {{ resultLabel }}
              </div>
              <div
                class="bj-result-amount"
                :class="{
                  win: payout.is_win,
                  loss: !payout.is_win && payout.net_cents < 0,
                }"
              >
                {{ resultAmountDisplay }}
              </div>
              <div v-if="resultReason" class="bj-result-reason">{{ resultReason }}</div>
              <div class="bj-result-hands" v-if="payout.hands && payout.hands.length > 1">
                <span
                  v-for="(h, i) in (payout.hands as HandPayoutData[])"
                  :key="i"
                  class="bj-result-hand-chip"
                  :class="h.net_cents > 0 ? 'win' : h.net_cents === 0 ? 'push' : 'loss'"
                >
                  Hand {{ i + 1 }}: {{ h.net_cents > 0 ? '+' : '' }}${{ (h.net_cents / 100).toFixed(2) }}
                </span>
              </div>
              <button class="btn-new-round" @click="resetRound">{{ $t('games.play_again') }}</button>
            </template>
          </div>

          <!-- Dealer area -->
          <div class="hand-area dealer-area">
            <span class="hand-label">{{ $t('games.dealer_label') }}</span>
            <div class="hand-cards">
              <TransitionGroup name="card-deal" tag="div" class="cards-row">
                <div
                  v-for="(card, idx) in dealerCards"
                  :key="dealerCardKey(card, idx)"
                  class="card"
                  :class="[
                    card === null ? 'facedown' : (isRedSuit(card.suit) ? 'red' : 'black'),
                    { 'card-spotlight': animRevealingIdx === idx && card !== null },
                  ]"
                >
                  <!-- Face-down hole card -->
                  <template v-if="card === null">
                    <div class="card-back-pattern"></div>
                  </template>
                  <!-- Face-up card -->
                  <template v-else>
                    <div class="card-corner">
                      <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="card-suit">{{ suitSymbol(card.suit) }}</span>
                    </div>
                    <div class="card-center">{{ suitSymbol(card.suit) }}</div>
                    <div class="card-corner-br">
                      <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="card-suit">{{ suitSymbol(card.suit) }}</span>
                    </div>
                  </template>
                </div>
              </TransitionGroup>
            </div>
            <div
              class="hand-value-badge"
              :class="{
                bust: dealerValueDisplay === 'Bust',
                blackjack: dealerValueDisplay === 'Blackjack',
                soft: dealerValueDisplay?.startsWith('Soft'),
              }"
              v-if="dealerValueDisplay"
            >
              {{ dealerValueDisplay }}
            </div>
          </div>

          <!-- Player hands area -->
          <div class="player-hands-area">
            <div
              v-for="(hand, hIdx) in playerHands"
              :key="`hand-${hIdx}`"
              class="hand-area player-area"
              :class="{
                'active-hand': hand.is_active,
                'split-hand': hand.is_split,
              }"
            >
              <div
                class="hand-value-badge"
                :class="{
                  bust: hand.value_display === 'Bust',
                  blackjack: hand.value_display === 'Blackjack',
                  soft: hand.value_display?.startsWith('Soft'),
                  stood: hand.status === 'stood' || hand.status === 'doubled',
                }"
                v-if="hand.value_display"
              >
                {{ hand.value_display }}
                <span v-if="hand.is_doubled" class="badge-sub">(×2)</span>
              </div>
              <div class="hand-cards">
                <TransitionGroup name="card-deal" tag="div" class="cards-row">
                  <div
                    v-for="(card, cIdx) in hand.cards"
                    :key="`hand-${hIdx}-card-${cIdx}`"
                    class="card"
                    :class="isRedSuit(card.suit) ? 'red' : 'black'"
                  >
                    <div class="card-corner">
                      <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="card-suit">{{ suitSymbol(card.suit) }}</span>
                    </div>
                    <div class="card-center">{{ suitSymbol(card.suit) }}</div>
                    <div class="card-corner-br">
                      <span class="card-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="card-suit">{{ suitSymbol(card.suit) }}</span>
                    </div>
                  </div>
                </TransitionGroup>
              </div>
              <span class="hand-label">
                {{ $t('games.player_label') }}{{ playerHands.length > 1 ? ` ${hIdx + 1}` : '' }}
                <span v-if="hand.status === 'surrendered'" class="hand-status-tag surrender">Surrendered</span>
                <span v-else-if="hand.status === 'stood'" class="hand-status-tag stood">Stand</span>
                <span v-else-if="hand.status === 'busted'" class="hand-status-tag bust">Bust</span>
              </span>
              <div v-if="hand.bet_cents" class="hand-bet">
                ${{ (hand.bet_cents / 100).toFixed(2) }}
              </div>
            </div>
          </div>

          <!-- Idle prompt -->
          <div v-if="roundState === 'idle'" class="bj-idle-prompt">
            <span>♠ Place your bet to start</span>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >{{ $t('games.bet_tab') }}</button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            @click="activeTab = 'rules'"
          >{{ $t('games.rules_tab') }}</button>
        </div>

        <!-- Bet Tab -->
        <div v-show="activeTab === 'bet'" class="game-tab-content">

          <!-- Chip-based betting interface (betting phase only) -->
          <template v-if="(roundState === 'idle' || roundState === 'complete') && !dealerAnimating">
            <label class="ctrl-label">Place Chips</label>
            <div class="chip-row">
              <button
                v-for="chip in CHIPS"
                :key="chip.valueCents"
                class="chip"
                :class="chip.cls"
                :title="`Add ${chip.label}`"
                @click="addChip(chip.valueCents)"
              >{{ chip.label }}</button>
            </div>

            <!-- Undo / Clear actions -->
            <div class="bet-shortcuts">
              <button
                class="bet-short"
                :disabled="chipHistory.length === 0"
                title="Remove last placed chip"
                @click="undoChip"
              >Undo</button>
              <button
                class="bet-short"
                :disabled="chipHistory.length === 0"
                title="Remove all chips"
                @click="clearChips"
              >Clear</button>
            </div>

            <div class="stat-pills">
              <div class="stat-pill">
                <div class="sp-label">Total Bet</div>
                <div class="sp-value">${{ (totalBetCents / 100).toFixed(2) }}</div>
              </div>
              <div class="stat-pill">
                <div class="sp-label">BJ Pays</div>
                <div class="sp-value gold">3:2</div>
              </div>
            </div>

            <!-- Validation warnings -->
            <div v-if="insufficientBalance" class="bet-warning">
              ⚠ Insufficient balance
            </div>

            <button
              class="btn-action"
              :disabled="!canDeal"
              @click="deal"
            >
              {{ loading ? 'DEALING...' : 'DEAL' }}
            </button>

            <button v-if="lastBetCents > 0" class="btn-rebet" @click="rebet">
              Rebet ${{ (lastBetCents / 100).toFixed(2) }}
            </button>
          </template>

          <!-- Action buttons (player turn) -->
          <template v-if="roundState === 'player_turn'">
            <div class="action-info">
              <div class="sp-label">Your Hand</div>
              <div class="sp-value gold">
                {{ activeHandDisplay }}
              </div>
            </div>

            <div class="action-grid">
              <button
                class="btn-action-sm btn-hit"
                :disabled="loading || !canHit"
                @click="takeAction('hit')"
                title="Draw another card"
              >
                HIT
              </button>
              <button
                class="btn-action-sm btn-stand"
                :disabled="loading"
                @click="takeAction('stand')"
                title="End your turn, dealer plays"
              >
                STAND
              </button>
              <button
                class="btn-action-sm btn-double"
                :disabled="loading || !canDoubleWithBalance"
                @click="takeAction('double')"
                :title="doubleTitle"
              >
                DOUBLE
              </button>
              <button
                class="btn-action-sm btn-split"
                :disabled="loading || !canSplitWithBalance"
                @click="takeAction('split')"
                :title="splitTitle"
              >
                SPLIT
              </button>
            </div>

            <button
              class="btn-action btn-insurance"
              :disabled="loading || !canInsurance"
              @click="takeInsuranceAction"
              :title="insuranceTitle"
            >
              INSURANCE (½ bet)
            </button>

            <button
              class="btn-action btn-surrender"
              :disabled="loading || !canSurrender"
              @click="takeAction('surrender')"
              :title="surrenderTitle"
            >
              SURRENDER
            </button>
          </template>

          <!-- Dealer auto-play hint (during animation or dealer turn) -->
          <template v-if="roundState === 'dealer_turn' || dealerAnimating">
            <div class="dealer-playing">
              <div class="dealer-playing-icon">♠</div>
              <div>{{ $t('games.blackjack.dealer_revealing') }}</div>
            </div>
          </template>

          <div class="balance-row">
            <span class="bal-label">Balance</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>

        <!-- Rules Tab -->
        <div v-show="activeTab === 'rules'" class="game-tab-content">
          <div class="rules-section">
            <div class="rules-title"><span><AppIcon name="game-blackjack" :size="18" /></span> {{ $t('games.slots.how_to_play') }}</div>
            <ul class="rules-list">
              <li class="rules-item">Get as close to 21 as possible without going over</li>
              <li class="rules-item">{{ $t('games.blackjack.rule_payout') }}</li>
              <li class="rules-item">{{ $t('games.blackjack.rule_dealer') }}</li>
              <li class="rules-item">{{ $t('games.blackjack.rule_double') }}</li>
              <li class="rules-item">{{ $t('games.blackjack.rule_split') }}</li>
              <li class="rules-item">Surrender: forfeit hand and recover half your bet</li>
              <li class="rules-item">Insurance: side bet when dealer shows Ace (pays 2:1)</li>
            </ul>
            <div class="rules-note">
              {{ $t('games.blackjack.rule_card_values') }}
            </div>
          </div>
          <div class="fair-tag"><AppIcon name="provably-fair" :size="12" /> Provably Fair</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'
import AppIcon from '@/components/AppIcon.vue'

// ─── Types ────────────────────────────────────────────────────────────────────

interface BjCard {
  rank: number
  suit: string
}

interface HandPayoutData {
  net_cents: number
  [key: string]: unknown
}

interface BjHandData {
  cards: BjCard[]
  bet_cents: number
  status: string
  is_doubled: boolean
  is_split: boolean
  value_display: string
  value: number
  is_active: boolean
}

interface BjDealerHand {
  cards: (BjCard | null)[]
  value_display: string
  status: string
}

interface BjPayout {
  total_bet_cents: number
  total_payout_cents: number
  is_win: boolean
  net_cents: number
  insurance_payout: number
  hands: unknown[]
}

interface RoundResponse {
  status: string
  bet_amount: number
  round_state: string
  active_hand_index: number
  player_hands: BjHandData[]
  dealer_hand: BjDealerHand
  insurance_bet_cents: number
  can_double: boolean
  can_split: boolean
  can_surrender: boolean
  can_insurance: boolean
  shoe_remaining: number
  payout?: BjPayout
}

type RoundState = 'idle' | 'player_turn' | 'dealer_turn' | 'complete'

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_BET_CENTS = 100      // $1 minimum bet
const MAX_BET_CENTS = 100_000  // $1000 maximum bet

const CHIPS = [
  { valueCents: 100,   label: '$1',   cls: 'chip-1' },
  { valueCents: 500,   label: '$5',   cls: 'chip-5' },
  { valueCents: 2500,  label: '$25',  cls: 'chip-25' },
  { valueCents: 10000, label: '$100', cls: 'chip-100' },
  { valueCents: 50000, label: '$500', cls: 'chip-500' },
]

const RANK_LABELS: Record<number, string> = {
  1: 'A', 11: 'J', 12: 'Q', 13: 'K',
}

const SUIT_SYMBOLS: Record<string, string> = {
  clubs: '♣', diamonds: '♦', hearts: '♥', spades: '♠',
}

// ─── Composables ──────────────────────────────────────────────────────────────

const { t } = useI18n()
const router      = useRouter()
const walletStore = useWalletStore()
const toastStore  = useToastStore()

// ─── State ────────────────────────────────────────────────────────────────────

const roundState     = ref<RoundState>('idle')
const playerHands    = ref<BjHandData[]>([])
const dealerHand     = ref<BjDealerHand | null>(null)
const activeHandIdx  = ref(0)
const canDouble      = ref(false)
const canSplit       = ref(false)
const canSurrender   = ref(false)
const canInsurance   = ref(false)
const shoeRemaining  = ref(0)
const payout         = ref<BjPayout | null>(null)
const chipHistory    = ref<number[]>([])  // chip values in cents, in placement order
const lastBetCents   = ref(0)
const loading        = ref(false)
const soundEnabled   = ref(true)
const activeTab      = ref<'bet' | 'rules'>('bet')

// ─── Dealer animation state ────────────────────────────────────────────────

const dealerAnimating  = ref(false)
const animDealerCards  = ref<(BjCard | null)[]>([])
const animRevealingIdx = ref<number | null>(null)
const resultVisible    = ref(false)

// ─── Computed ─────────────────────────────────────────────────────────────────

const dealerCards = computed<(BjCard | null)[]>(() =>
  dealerAnimating.value ? animDealerCards.value : (dealerHand.value?.cards ?? [])
)

const dealerValueDisplay = computed(() => dealerHand.value?.value_display ?? '')

const formattedBalance = computed(() => {
  const bal = walletStore.balance?.balance
  if (bal == null) return '—'
  return `$${bal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
})

const totalBetCents = computed(() => chipHistory.value.reduce((s, c) => s + c, 0))

const balanceCents = computed(() => Math.round((walletStore.balance?.balance ?? 0) * 100))

const insufficientBalance = computed(
  () => totalBetCents.value > 0 && totalBetCents.value > balanceCents.value,
)

const canDeal = computed(
  () => !loading.value && totalBetCents.value >= MIN_BET_CENTS && !insufficientBalance.value,
)

const activeHandDisplay = computed(() => {
  const hand = playerHands.value[activeHandIdx.value]
  return hand?.value_display ?? ''
})

const resultLabel = computed(() => {
  if (!payout.value) return ''
  if (payout.value.is_win) return 'YOU WIN'
  if (payout.value.net_cents === 0) return 'PUSH'
  return 'YOU LOSE'
})

const resultAmountDisplay = computed(() => {
  if (!payout.value) return ''
  const net = payout.value.net_cents / 100
  if (net > 0) return `+$${net.toFixed(2)}`
  if (net === 0) return 'Bet Returned'
  return `-$${Math.abs(net).toFixed(2)}`
})

const resultReason = computed(() => {
  if (!payout.value) return ''
  if (payout.value.net_cents === 0) return t('games.push_tie')
  if (payout.value.is_win) {
    const hand = playerHands.value[0]
    if (hand?.status === 'blackjack') return t('games.blackjack.blackjack_payout')
    if (dealerHand.value?.status === 'busted') return t('games.blackjack.dealer_bust')
    return ''
  }
  if (dealerHand.value?.status === 'blackjack') return t('games.blackjack.dealer_blackjack')
  return t('games.blackjack.dealer_wins')
})

const canHit = computed(() => {
  if (roundState.value !== 'player_turn') return false
  const hand = playerHands.value[activeHandIdx.value]
  if (!hand) return false
  return hand.value < 21 && hand.status === 'active'
})

const canDoubleWithBalance = computed(() => {
  if (!canDouble.value) return false
  const hand = playerHands.value[activeHandIdx.value]
  if (!hand) return false
  const bal = walletStore.balance?.balance ?? 0
  return bal >= hand.bet_cents / 100
})

const canSplitWithBalance = computed(() => {
  if (!canSplit.value) return false
  const hand = playerHands.value[activeHandIdx.value]
  if (!hand) return false
  const bal = walletStore.balance?.balance ?? 0
  return bal >= hand.bet_cents / 100
})

const doubleTitle = computed(() => {
  const hand = playerHands.value[activeHandIdx.value]
  if (!canDouble.value) return t('games.blackjack.cannot_double')
  if (hand) {
    const bal = walletStore.balance?.balance ?? 0
    if (bal < hand.bet_cents / 100) return t('games.blackjack.insufficient_balance_double')
  }
  return t('games.blackjack.double_tooltip')
})

const splitTitle = computed(() => {
  const hand = playerHands.value[activeHandIdx.value]
  if (!canSplit.value) return t('games.blackjack.cannot_split')
  if (hand) {
    const bal = walletStore.balance?.balance ?? 0
    if (bal < hand.bet_cents / 100) return t('games.blackjack.insufficient_balance_split')
  }
  return t('games.blackjack.split_tooltip')
})

const insuranceTitle = computed(() => {
  if (!canInsurance.value) return t('games.blackjack.insurance_dealer_ace')
  const hand = playerHands.value[activeHandIdx.value]
  if (hand) {
    const bal = walletStore.balance?.balance ?? 0
    if (bal < hand.bet_cents / 100 / 2) return t('games.blackjack.insufficient_balance_insurance')
  }
  return t('games.blackjack.insurance_tooltip')
})

const surrenderTitle = computed(() => {
  if (!canSurrender.value) return 'Surrender only allowed on first two cards'
  return 'Forfeit hand, recover half your bet'
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function rankLabel(rank: number): string {
  return RANK_LABELS[rank] ?? String(rank)
}

function suitSymbol(suit: string): string {
  return SUIT_SYMBOLS[suit] ?? suit
}

function isRedSuit(suit: string): boolean {
  return suit === 'hearts' || suit === 'diamonds'
}

function dealerCardKey(card: BjCard | null, idx: number): string {
  return card === null ? `dealer-${idx}-back` : `dealer-${idx}-${card.rank}-${card.suit}`
}

function addChip(cents: number): void {
  if (totalBetCents.value + cents > MAX_BET_CENTS) return
  chipHistory.value = [...chipHistory.value, cents]
}

function undoChip(): void {
  if (chipHistory.value.length === 0) return
  chipHistory.value = chipHistory.value.slice(0, -1)
}

function clearChips(): void {
  chipHistory.value = []
}

function rebet(): void {
  if (lastBetCents.value <= 0) return
  chipHistory.value = [lastBetCents.value]
}

// ─── Round management ─────────────────────────────────────────────────────────

function applyRoundResponse(data: RoundResponse): void {
  const wasPlayerTurn   = roundState.value === 'player_turn'
  const prevDealerCards = dealerHand.value?.cards ?? []

  roundState.value    = data.round_state as RoundState
  playerHands.value   = data.player_hands
  activeHandIdx.value = data.active_hand_index
  canDouble.value     = data.can_double
  canSplit.value      = data.can_split
  canSurrender.value  = data.can_surrender
  canInsurance.value  = data.can_insurance
  shoeRemaining.value = data.shoe_remaining
  payout.value        = data.payout ?? null

  if (data.round_state === 'complete') {
    const finalCards = data.dealer_hand.cards as (BjCard | null)[]
    const hadHiddenHoleCard = prevDealerCards.length >= 2 && prevDealerCards[1] === null
    const hasNewCards       = finalCards.length > prevDealerCards.length

    if (wasPlayerTurn && (hadHiddenHoleCard || hasNewCards)) {
      // Animate dealer reveal: start from previous visible state, reveal cards one-by-one
      dealerAnimating.value  = true
      resultVisible.value    = false
      animDealerCards.value  = [...prevDealerCards]
      // Keep full dealer info (status/value_display) but cards driven by animDealerCards
      dealerHand.value = data.dealer_hand

      const steps: Array<{ delay: number; action: () => void }> = []
      let totalDelay = 150

      // Step 1: reveal hidden hole card (was null → actual card)
      if (hadHiddenHoleCard) {
        const holeCard = finalCards[1]
        const d = totalDelay
        steps.push({
          delay: d,
          action: () => {
            animRevealingIdx.value = 1
            const next = [...animDealerCards.value]
            next[1] = holeCard
            animDealerCards.value = next
            setTimeout(() => { animRevealingIdx.value = null }, 550)
          },
        })
        totalDelay += 520
      }

      // Step 2: add any extra dealer draw cards (dealer hit on soft/hard 16)
      const startIdx = Math.max(prevDealerCards.length, 2)
      for (let i = startIdx; i < finalCards.length; i++) {
        const card = finalCards[i]
        const cardIdx = i
        const d = totalDelay
        steps.push({
          delay: d,
          action: () => {
            animRevealingIdx.value = cardIdx
            animDealerCards.value = [...animDealerCards.value, card]
            setTimeout(() => { animRevealingIdx.value = null }, 550)
          },
        })
        totalDelay += 520
      }

      // Schedule all steps
      steps.forEach(({ delay, action }) => setTimeout(action, delay))

      // Show result overlay after all cards are revealed
      setTimeout(() => {
        dealerAnimating.value = false
        resultVisible.value   = true
      }, totalDelay + 250)
    } else {
      // No animation needed (e.g. natural BJ on initial deal)
      dealerHand.value    = data.dealer_hand
      animDealerCards.value = []
      // Brief delay to let deal animation play before overlay appears
      const delay = wasPlayerTurn ? 0 : 700
      setTimeout(() => { resultVisible.value = true }, delay)
    }
  } else {
    dealerHand.value  = data.dealer_hand
    resultVisible.value = false
  }
}

async function resetRound(): Promise<void> {
  roundState.value       = 'idle'
  playerHands.value      = []
  dealerHand.value       = null
  canDouble.value        = false
  canSplit.value         = false
  canSurrender.value     = false
  canInsurance.value     = false
  payout.value           = null
  dealerAnimating.value  = false
  animDealerCards.value  = []
  animRevealingIdx.value = null
  resultVisible.value    = false
  chipHistory.value      = []  // Clear chips so player places fresh bet (use Rebet to restore)
}

// ─── API calls ────────────────────────────────────────────────────────────────

async function deal(): Promise<void> {
  if (!canDeal.value) return

  loading.value = true
  try {
    const res = await apiClient.post<RoundResponse>('/api/games/blackjack/deal', {
      amount: (totalBetCents.value / 100).toFixed(2),
    })
    lastBetCents.value = totalBetCents.value
    applyRoundResponse(res.data)
    walletStore.applyGameResult(totalBetCents.value / 100, 0)
  } catch (err: unknown) {
    const axiosError = err as { response?: { data?: { message?: string } } }
    toastStore.error(axiosError?.response?.data?.message ?? 'Failed to deal. Please try again.')
  } finally {
    loading.value = false
  }
}

async function takeAction(action: string): Promise<void> {
  if (loading.value) return
  loading.value = true
  try {
    const res = await apiClient.post<RoundResponse>('/api/games/blackjack/action', { action })
    applyRoundResponse(res.data)
    if (res.data.round_state === 'complete' && res.data.payout) {
      walletStore.applyGameResult(0, res.data.payout.total_payout_cents / 100)
    }
  } catch (err: unknown) {
    const axiosError = err as { response?: { data?: { message?: string } } }
    toastStore.error(axiosError?.response?.data?.message ?? 'Action failed. Please try again.')
  } finally {
    loading.value = false
  }
}

async function takeInsuranceAction(): Promise<void> {
  const activeHand = playerHands.value[activeHandIdx.value]
  if (!activeHand) return
  const insBet = (activeHand.bet_cents / 100 / 2).toFixed(2)
  loading.value = true
  try {
    const res = await apiClient.post<RoundResponse>('/api/games/blackjack/action', {
      action: 'insurance',
      amount: insBet,
    })
    applyRoundResponse(res.data)
    walletStore.applyGameResult(parseFloat(insBet), 0)
    toastStore.success('Insurance placed')
  } catch (err: unknown) {
    const axiosError = err as { response?: { data?: { message?: string } } }
    toastStore.error(axiosError?.response?.data?.message ?? 'Insurance failed.')
  } finally {
    loading.value = false
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
  if (!walletStore.balance) walletStore.fetchBalance()

  try {
    const res = await apiClient.get<RoundResponse & { status: string }>('/api/games/blackjack/state')
    if (res.data.status === 'pending') {
      applyRoundResponse(res.data)
    }
  } catch {
    // No active game — stay idle
  }
})
</script>

<style scoped>
/* ---- Layout ---- */
.bj-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  animation: fadeIn 0.25s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ---- Topbar ---- */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-back:hover { color: var(--color-on-dark, #fff); background: var(--bg-hover, #2a4455); }

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
}

.bj-tag {
  background: rgba(255, 215, 0, 0.1);
  color: var(--accent-gold, #ffd700);
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  border: none;
}
.icon-btn:hover, .icon-btn.active {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
}

/* ---- Game body ---- */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* ---- Canvas / Felt Table ---- */
.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.bj-table {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  background: radial-gradient(ellipse at center, #0d3320 0%, #0a2618 60%, var(--bg-game, #0d1b2a) 100%);
  padding: 24px 32px;
  position: relative;
}

.bj-felt-edge {
  position: absolute;
  inset: 0;
  border-radius: 0;
  pointer-events: none;
  background: radial-gradient(ellipse at center, transparent 60%, rgba(0, 0, 0, 0.3) 100%);
}

.shoe-info {
  position: absolute;
  top: 10px;
  right: 16px;
  font-size: 10px;
  font-family: 'JetBrains Mono', monospace;
  color: rgba(255, 255, 255, 0.35);
  z-index: 1;
}

/* ---- Result Overlay ---- */
.bj-result-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(10, 38, 24, 0.88);
  backdrop-filter: blur(4px);
  z-index: 20;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.35s ease;
  gap: 6px;
}
.bj-result-overlay.active {
  opacity: 1;
  pointer-events: all;
}

.bj-result-text {
  font-family: 'Russo One', sans-serif;
  font-size: 48px;
  animation: winPop 0.45s ease both;
  line-height: 1;
  margin-bottom: 4px;
}
.bj-result-text.win  { color: var(--color-success, #00e701); text-shadow: 0 0 40px rgba(0, 231, 1, 0.5); }
.bj-result-text.loss { color: var(--color-danger, #ed4245);  text-shadow: 0 0 40px rgba(237, 66, 69, 0.5); }
.bj-result-text.push { color: var(--accent-gold, #ffd700);   text-shadow: 0 0 30px rgba(255, 215, 0, 0.4); }

.bj-result-amount {
  font-family: 'JetBrains Mono', monospace;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}
.bj-result-amount.win  { color: var(--color-success, #00e701); }
.bj-result-amount.loss { color: var(--color-danger, #ed4245); }

.bj-result-reason {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent-gold, #ffd700);
  margin-top: 2px;
}

.bj-result-hands {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 4px;
}

.bj-result-hand-chip {
  font-size: 11px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
  padding: 3px 10px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.08);
}
.bj-result-hand-chip.win  { color: var(--color-success, #00e701); background: rgba(0, 231, 1, 0.1); }
.bj-result-hand-chip.loss { color: var(--color-danger, #ed4245);  background: rgba(237, 66, 69, 0.1); }
.bj-result-hand-chip.push { color: var(--accent-gold, #ffd700);   background: rgba(255, 215, 0, 0.1); }

.btn-new-round {
  margin-top: 20px;
  padding: 12px 32px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-new-round:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.4);
  transform: translateY(-1px);
}

@keyframes winPop {
  0%   { transform: scale(0.7);   opacity: 0; }
  60%  { transform: scale(1.12); }
  100% { transform: scale(1);    opacity: 1; }
}

/* ---- Hand areas ---- */
.hand-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 1;
  position: relative;
}

.dealer-area {
  flex-direction: column;
}

.player-hands-area {
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
}

.player-area {
  flex-direction: column-reverse; /* value badge below cards, label below that */
}

.hand-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 6px;
}

.hand-bet {
  font-size: 11px;
  font-family: 'JetBrains Mono', monospace;
  color: var(--accent-gold, #ffd700);
  font-weight: 700;
}

.hand-status-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 4px;
}
.hand-status-tag.surrender { background: rgba(237, 66, 69, 0.2);  color: var(--color-danger, #ed4245); }
.hand-status-tag.stood     { background: rgba(20, 117, 225, 0.2); color: var(--color-info-light, #6ab0f5); }
.hand-status-tag.bust      { background: rgba(237, 66, 69, 0.2);  color: var(--color-danger, #ed4245); }

/* Active hand highlight */
.active-hand .hand-label {
  color: var(--accent-primary, #00e701);
}
.active-hand::before {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 12px;
  border: 2px solid rgba(0, 231, 1, 0.4);
  pointer-events: none;
  animation: activeGlow 2s ease infinite;
}

@keyframes activeGlow {
  0%, 100% { border-color: rgba(0, 231, 1, 0.4); }
  50%       { border-color: rgba(0, 231, 1, 0.8); }
}

/* ---- Cards row ---- */
.cards-row {
  display: flex;
  align-items: center;
  min-height: 108px;
}

/* ---- Playing card ---- */
.card {
  width: 74px;
  height: 104px;
  border-radius: 8px;
  background: var(--color-on-dark, #fff);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.card + .card {
  margin-left: -18px;
}

.card.red   { color: var(--card-red, #d32f2f); }
.card.black { color: var(--roulette-black-dark, #111111); }

.card.facedown {
  background: linear-gradient(135deg, #1475E1 0%, #0d5abf 100%);
  border: 2px solid rgba(255, 255, 255, 0.15);
}

.card-back-pattern {
  width: 100%;
  height: 100%;
  background-image:
    repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1) 0px,
      rgba(255, 255, 255, 0.1) 2px,
      transparent 2px,
      transparent 8px
    );
  border-radius: 4px;
}

.card-corner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
}

.card-corner-br {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  align-self: flex-end;
  transform: rotate(180deg);
}

.card-rank {
  font-size: 13px;
  font-weight: 900;
  font-family: 'JetBrains Mono', monospace;
  line-height: 1;
}

.card-suit {
  font-size: 11px;
  line-height: 1;
}

.card-center {
  font-size: 32px;
  text-align: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ---- Hand value badge ---- */
.hand-value-badge {
  background: rgba(0, 0, 0, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 3px 10px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  display: flex;
  align-items: center;
  gap: 4px;
}
.hand-value-badge.bust      { color: var(--color-danger, #ed4245); border-color: rgba(237, 66, 69, 0.4); }
.hand-value-badge.blackjack { color: var(--accent-gold, #ffd700);  border-color: rgba(255, 215, 0, 0.4); }
.hand-value-badge.soft      { color: var(--color-info-light, #7ecbf5); }
.hand-value-badge.stood     { opacity: 0.65; }

.badge-sub {
  font-size: 10px;
  opacity: 0.75;
}

/* ---- Idle prompt ---- */
.bj-idle-prompt {
  position: absolute;
  bottom: 32px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 0.04em;
  pointer-events: none;
}

/* ---- Card deal animation (Vue TransitionGroup) ---- */
.card-deal-enter-active {
  transition: all 0.38s cubic-bezier(0.22, 0.61, 0.36, 1);
}
.card-deal-enter-from {
  opacity: 0;
  transform: translateY(-40px) scale(0.8) rotateY(90deg);
}
.card-deal-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1) rotateY(0deg);
}
.card-deal-leave-active {
  transition: all 0.2s ease;
}
.card-deal-leave-to {
  opacity: 0;
  transform: scale(0.6) rotateY(90deg);
}

/* ---- Card spotlight (hole-card reveal glow) ---- */
.card.card-spotlight {
  animation: cardSpotlight 0.55s ease;
}

@keyframes cardSpotlight {
  0%   { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6), 0 0   0px rgba(255, 215, 0, 0);   }
  40%  { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6), 0 0  32px rgba(255, 215, 0, 0.7); }
  100% { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.3);     }
}

/* ---- Sidebar ---- */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}
.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}
.game-tab:hover:not(.active) { color: var(--text-secondary, #b1bad3); }

.game-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.game-tab-content::-webkit-scrollbar { width: 3px; }

/* ---- Controls ---- */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}

.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}
.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;
  text-align: center;
  font-family: inherit;
}
.bet-short:hover:not(:disabled) { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.bet-short:disabled { opacity: 0.35; cursor: not-allowed; }

.bet-warning {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-warning, #ffa726);
  background: rgba(255, 167, 38, 0.08);
  border: 1px solid rgba(255, 167, 38, 0.2);
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 6px;
}

/* Chip row */
.chip-row {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.chip {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 150ms ease;
  flex-shrink: 0;
  font-family: inherit;
  color: var(--color-on-dark, #fff);
}
.chip:hover {
  transform: scale(1.12);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 14px rgba(0, 0, 0, 0.6);
}
.chip-1   { background: linear-gradient(135deg, #4a4a4a, #2a2a2a); }
.chip-5   { background: linear-gradient(135deg, #1475E1, #0d5abf); }
.chip-25  { background: linear-gradient(135deg, #00E701, #00aa00); color: var(--color-on-accent, #000); }
.chip-100 { background: linear-gradient(135deg, #FFD700, #cc9900); color: var(--color-on-accent, #000); }
.chip-500 { background: linear-gradient(135deg, var(--accent-purple, #9B59B6), #7d3f9a); }

/* Stat pills */
.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 10px;
}
.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}
.sp-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}
.sp-value.gold { color: var(--accent-gold, #ffd700); }

/* Action buttons */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}
.btn-action:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-action:active:not(:disabled) { transform: translateY(0); }

.btn-rebet {
  width: 100%;
  padding: 10px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 150ms ease;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-rebet:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

/* In-play action info */
.action-info {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

/* In-play action grid */
.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 8px;
}

.btn-action-sm {
  padding: 12px 4px;
  font-size: 12px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.04em;
  font-family: inherit;
}
.btn-action-sm:disabled { opacity: 0.35; cursor: not-allowed; }

.btn-hit    { background: var(--accent-primary, #00e701); color: var(--color-on-accent, #000); }
.btn-hit:hover:not(:disabled)    { background: var(--accent-primary-hover, #00cc01); box-shadow: 0 0 16px rgba(0, 231, 1, 0.3); }

.btn-stand  { background: var(--accent-secondary, #1475E1); color: var(--color-on-dark, #fff); }
.btn-stand:hover:not(:disabled)  { background: var(--accent-secondary-hover, #1060c0); box-shadow: 0 0 16px rgba(20, 117, 225, 0.3); }

.btn-double { background: var(--color-warning, #ffa726); color: var(--color-on-accent, #000); }
.btn-double:hover:not(:disabled) { background: var(--color-warning-hover, #e09000); box-shadow: 0 0 16px rgba(255, 167, 38, 0.3); }

.btn-split  { background: var(--accent-purple, #9B59B6); color: var(--color-on-dark, #fff); }
.btn-split:hover:not(:disabled)  { background: var(--accent-purple-hover, #7d3f9a); box-shadow: 0 0 16px rgba(155, 89, 182, 0.3); }

.btn-insurance {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold, #ffd700);
  border: 1px solid rgba(255, 215, 0, 0.3);
  font-size: 13px;
}
.btn-insurance:hover:not(:disabled) {
  background: rgba(255, 215, 0, 0.25);
  box-shadow: 0 0 16px rgba(255, 215, 0, 0.2);
  transform: translateY(-1px);
}

.btn-surrender {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border: 1px solid rgba(237, 66, 69, 0.3);
  font-size: 13px;
}
.btn-surrender:hover:not(:disabled) {
  background: rgba(237, 66, 69, 0.25);
  box-shadow: 0 0 16px rgba(237, 66, 69, 0.2);
  transform: translateY(-1px);
}

/* Dealer playing hint */
.dealer-playing {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-gold, #ffd700);
  padding: 16px 10px;
  margin-bottom: 8px;
  background: rgba(255, 215, 0, 0.08);
  border-radius: 8px;
  border: 1px solid rgba(255, 215, 0, 0.2);
}
.dealer-playing-icon {
  font-size: 22px;
  animation: pulse 1.2s ease infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
}

/* Balance row */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
}
.bal-label { font-size: 11px; color: var(--text-tertiary, #7f8fa4); }
.bal-value { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; }
.bal-value.green { color: var(--color-success, #00e701); }

/* ---- Rules ---- */
.rules-section { margin-bottom: 16px; }
.rules-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.rules-list { list-style: none; display: flex; flex-direction: column; gap: 5px; }
.rules-item {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  padding-left: 14px;
  position: relative;
  line-height: 1.55;
}
.rules-item::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: var(--accent-primary, #00e701);
  font-size: 10px;
  top: 1px;
}
.rules-note {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 10px;
  margin-top: 10px;
  line-height: 1.6;
  border-left: 2px solid var(--accent-primary, #00e701);
}

.fair-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(20, 117, 225, 0.1);
  border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--accent-secondary, #1475E1);
  margin-top: 12px;
  cursor: pointer;
}

/* ---- Responsive ---- */
@media (max-width: 900px) {
  .game-sidebar { width: 240px; }
}

@media (max-width: 768px) {
  .game-body { flex-direction: column; }
  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 320px;
  }
  .bj-table { padding: 16px; gap: 16px; }
  .card { width: 62px; height: 88px; }
  .card + .card { margin-left: -14px; }
  .card-center { font-size: 26px; }
}
</style>
